import {
  IP_INFO_SERVICE,
  PATIENT_DEFAULT_SECRET_ANSWER,
  PATIENT_DEFAULT_SECRET_QUESTION_ID,
  PHONE_SERVICE,
} from '@configs';
import { TranslationKeys } from '@ui-core/types';
import { extractMessageFromError, logError, processEmail } from '@utils';

import { APIClient } from '../api-client';
import { Encryption } from '../encryption-service';
import { OTP_SENT_MESSAGE } from './global-api-service.constant';
import {
  AlternativeIPServiceResponse,
  APIErrors,
  CreateAccountGloballyParams,
  GenerateCertificatePayload,
  GenerateCertificateResponse,
  GetFirebaseDataForQRCodePayload,
  GetFirebaseResponse,
  GetPatientDataFromInvitationQRCodeParams,
  LoginPayload,
  LoginResponse,
  MainIPServiceResponse,
  RegisterPayload,
  RegisterResponse,
  ResendLoginCodePayload,
  ResendLoginCodeResponse,
  Verify2FACodePayload,
  Verify2FACodeResponse,
  VerifyResetRequest,
  VerifyResetResponse,
} from './global-api-service.types';
import { extractPatientInformation } from './global-api-service.util';

export class GlobalAPIService {
  static readonly login = async (email: string, password: string) => {
    const { data } = await APIClient.post<LoginResponse, LoginPayload>({
      url: '/sso/login',
      payload: {
        emailHash: Encryption.generateHash(email),
        passwordHash: Encryption.generateHash(password),
        email: Encryption.encrypt(email),
      },
    });

    if (data.message === OTP_SENT_MESSAGE) return;

    return {
      primarySourceUuid: data.firebasePatientId,
      primarySourceId: data.firebaseProjectId,
      uuid: data.patientUUID,
      isAccountVerified: data.isVerified,
      is2faActive: false,
      accessToken: data.accessToken,
    };
  };

  static readonly validateLogin = async (email: string, password: string) => {
    const { data } = await APIClient.post<LoginResponse, LoginPayload>({
      url: '/sso/login',
      payload: {
        emailHash: Encryption.generateHash(email),
        passwordHash: Encryption.generateHash(password),
        email: Encryption.encrypt(email),
        isRelogin: true,
      },
    });
    return data.accessToken;
  };

  static readonly validate2FACode = async (email: string, code: string) =>
    await APIClient.post<Verify2FACodeResponse, Verify2FACodePayload>({
      url: '/sso/login/verify-otp',
      payload: {
        emailHash: Encryption.generateHash(processEmail(email)),
        pinCode: Encryption.encrypt(code),
      },
    });

  static readonly resendLoginCode = async (email: string) => {
    return await APIClient.post<
      ResendLoginCodeResponse,
      ResendLoginCodePayload
    >({
      url: '/sso/send-code',
      payload: {
        emailHash: Encryption.generateHash(email),
        email: Encryption.encrypt(email),
      },
    });
  };

  static readonly verifyResetPin = async (email: string, pin: string) => {
    return await APIClient.post<VerifyResetResponse, VerifyResetRequest>({
      url: '/sso/password-recovery/verify-otp',
      payload: {
        emailHash: Encryption.generateHash(email),
        pinCode: Encryption.encrypt(pin),
      },
    });
  };

  static readonly resetPassword = async (
    email: string,
    password: string,
    token: string
  ) => {
    return await APIClient.post({
      url: '/sso/password-recovery/change-password',
      payload: {
        email: Encryption.encrypt(email),
        passwordHash: Encryption.generateHash(password),
        accessToken: token,
        signature: token,
      },
    });
  };

  static readonly getFirebaseData = async (
    projectId: string,
    qrCodeUUID: string
  ) => {
    const payload = Encryption.encrypt({
      projectId,
      qrCodeUUID,
    });

    const result = await APIClient.post<string, { payload: string }>({
      url: '/firebaseManager/generateAdvancedCustomTokenQrCode',
      payload: { payload },
    });

    return Encryption.decryptAndParseObject<GetFirebaseResponse>(result.data);
  };

  static readonly getFirebaseDataForQRCode = async ({
    projectId,
    qrCodeUUID,
  }: GetFirebaseDataForQRCodePayload) => {
    const payload = Encryption.encrypt({
      projectId,
      qrCodeUUID,
    });

    const result = await APIClient.post<string, { payload: string }>({
      url: '/firebaseManager/generateCustomTokenForQrCode',
      payload: { payload },
    });

    return Encryption.decryptAndParseObject<string>(result.data);
  };

  static readonly getCustomToken = async (
    patientUuid: string,
    databaseURL: string
  ) => {
    const payload = Encryption.encrypt({
      firebasePatientId: patientUuid,
      databaseURL: databaseURL,
    });

    const result = await APIClient.post<string, { payload: string }>({
      url: '/firebaseManager/generateAdvancedCustomToken',
      payload: { payload },
    });

    const decryptedToken = Encryption.decryptAndParseObject<{
      customToken: string;
    }>(result.data);

    return decryptedToken;
  };

  static readonly getPublicIpInformationFromMainService = async () => {
    return await APIClient.get<MainIPServiceResponse>({
      url: IP_INFO_SERVICE,
    });
  };

  static readonly getPublicIpInformationFromAlternativeService = async (
    url: string
  ) => {
    return await APIClient.get<AlternativeIPServiceResponse>({
      url: url,
    });
  };

  static readonly generateCertificate = async (
    payload: GenerateCertificatePayload
  ) => {
    return await APIClient.post<
      GenerateCertificateResponse,
      GenerateCertificatePayload
    >({
      url: '/registerPhonePatient',
      payload,
      baseURL: PHONE_SERVICE,
      timeout: 3000,
    });
  };

  static readonly generatePatientUUID = async () => {
    const payload = { type: 3, retryCount: 1 };
    return (
      await APIClient.post<string, { type: number; retryCount: number }>({
        url: 'uuid/generate',
        payload,
      })
    ).data;
  };

  static readonly checkEmailExists = async (email: string) => {
    const payload = {
      emailHash: Encryption.generateHash(email),
    };
    const { data: emailExists } = await APIClient.post({
      url: '/sso/checkIfEmailExist',
      payload,
    });
    return !emailExists;
  };

  static readonly createAccountGlobally = async ({
    email,
    password,
    patientSourceUUID,
    sourceProjectId,
  }: CreateAccountGloballyParams) => {
    const secretHash = PATIENT_DEFAULT_SECRET_ANSWER;
    const emailHash = Encryption.generateHash(email);
    const passwordHash = Encryption.generateHash(password);
    const signature = Encryption.generateHash(password + secretHash);
    const secretQuestionAnswerHash = Encryption.createSecretAnswerHash(
      email,
      secretHash
    );

    const { status: createStatus, data } = await APIClient.post<
      RegisterResponse,
      RegisterPayload
    >({
      url: `/sso/register`,
      payload: {
        emailHash,
        signature,
        firebaseProjectId: sourceProjectId,
        patientUUID: patientSourceUUID,
        firebasePatientId: patientSourceUUID,
        secretQuestionId: PATIENT_DEFAULT_SECRET_QUESTION_ID,
        passwordHash,
        secretQuestionAnswerHash,
      },
    });

    if (createStatus !== 200) throw new Error('general.unexpectedError');

    const { status: verifyStatus } = await APIClient.post({
      url: '/sso/verifyTrustedInstallation',
      payload: {
        emailHash,
      },
    });

    if (verifyStatus !== 200) throw new Error('general.unexpectedError');

    return data.accessToken;
  };

  static readonly getPatientDataFromInvitationQRCode = async ({
    patientSourceUUID,
    siteId,
    instanceUrl,
  }: GetPatientDataFromInvitationQRCodeParams) => {
    let payload: string;
    try {
      payload = Encryption.encrypt({
        firebasePatientId: patientSourceUUID,
        siteId,
      });
    } catch (e) {
      logError(e);
      throw new Error(
        'auth.register.authenticate.incorrectPin' as TranslationKeys
      );
    }

    try {
      const { data } = await APIClient.post<string, { payload: string }>({
        url: `/broker/patients/getPatientConnectionData`,
        payload: { payload },
        baseURL: instanceUrl,
      });
      const decryptedData = Encryption.decrypt(data);
      return extractPatientInformation(decryptedData as string);
    } catch (e) {
      logError(e);
      const message = extractMessageFromError(e, {
        [APIErrors.RegLetterScanningForbidden]:
          'auth.register.authenticate.scanningNotAllowed',
      });
      throw new Error(message);
    }
  };

  static readonly deletePatientFromGlobalService = async (
    password: string,
    uuid?: string
  ) => {
    try {
      const payload = {
        patientUUID: uuid,
        passwordHash: Encryption.generateHash(password),
      };
      const response = await APIClient.post({
        url: '/sso/deleteAccount',
        payload,
      });
      return response;
    } catch (error) {
      logError(error);
      throw error;
    }
  };

  static readonly toggle2FA = async (email: string, accessToken: string) => {
    return APIClient.post({
      url: '/sso/login/toggle-otp',
      payload: {
        emailHash: Encryption.generateHash(email),
        accessToken,
      },
    });
  };
}
